/**
 * @file Bidder table for an auction group: filters, expand lots, scan/print.
 */

import React, { Suspense, useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getBidders, getBidderByInvoice } from '../api/bidders';
import { getAuctionGroupById } from '../api/auctionGroups';
import { queryKeys } from '../api/queryKeys';
import PaginationControls from '../components/PaginationControls';
import { Loader, Scan, ArrowLeft, Package, X, FileUp, BarChart3 } from 'lucide-react';
import BidderHistoryModal from '../components/BidderHistoryModal';
import BidderRow from '../components/BidderRow';
import PrintFormatToggle from '../components/PrintFormatToggle';
import ConnectionStatusBar from '../components/ConnectionStatusBar';
import { usePickQueue } from '../hooks/usePickQueue';
import { usePrintFormatPreference } from '../hooks/usePrintFormatPreference';
import {
  PICK_FIELD_LOADED_OUT,
  PICK_FIELD_MARKED_PICKED,
  applyPendingPicks,
  clearPickQueueError,
  enqueuePick,
} from '../utils/pickQueue';
import { decodeHtmlEntities } from '../utils/htmlDecode';
import { DEFAULT_TABLE_PAGE_SIZE } from '../utils/pagination';
import { INPUT_MAX_LENGTH, clampInput } from '../utils/inputLimits';
import { APP_HOME } from '../constants';
import { isPickSheetScanUrl, parseInvoiceBarcode } from '../utils/parseInvoiceBarcode';
import { setAutoOpenScanCamera } from '../utils/scanCameraHandoff';

const BarcodeScanner = React.lazy(() => import('../components/BarcodeScanner'));

function ViewBidders() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const [filters, setFilters] = useState({
    bidderId: '',
    name: '',
    invoiceNumber: '',
    auctionGroupId: null,
  });
  const [debouncedText, setDebouncedText] = useState({
    bidderId: '',
    name: '',
    invoiceNumber: '',
  });
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [loadedOutFilter, setLoadedOutFilter] = useState('all'); // 'all', 'loaded', 'not-loaded'
  const [markedPickedFilter, setMarkedPickedFilter] = useState('all'); // 'all', 'picked', 'not-picked'
  const [scanStatusFilter, setScanStatusFilter] = useState('all'); // 'all', 'scanned', 'partial', 'not-scanned'
  const [scanModeActive, setScanModeActive] = useState(false);
  const [scanInput, setScanInput] = useState('');
  const [isMobile, setIsMobile] = useState(false);
  const [scanGroupWarning, setScanGroupWarning] = useState('');
  const [historyBidder, setHistoryBidder] = useState(null);
  const [limit, setLimit] = useState(DEFAULT_TABLE_PAGE_SIZE);
  const [offset, setOffset] = useState(0);
  const scanLookupInFlightRef = useRef(false);

  // Detect mobile device
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Debounce text filters before hitting the API (typing fires expensive group aggregates)
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedText({
        bidderId: filters.bidderId,
        name: filters.name,
        invoiceNumber: filters.invoiceNumber,
      });
    }, 450);
    return () => clearTimeout(t);
  }, [filters.bidderId, filters.name, filters.invoiceNumber]);

  // Reset to first page when filters/sort/group change
  useEffect(() => {
    setOffset(0);
  }, [
    filters.auctionGroupId,
    debouncedText.bidderId,
    debouncedText.name,
    debouncedText.invoiceNumber,
    loadedOutFilter,
    markedPickedFilter,
    scanStatusFilter,
    sortConfig.key,
    sortConfig.direction,
  ]);

  const queryFilters = useMemo(() => ({
    auctionGroupId: filters.auctionGroupId,
    bidderId: debouncedText.bidderId || undefined,
    name: debouncedText.name || undefined,
    invoiceNumber: debouncedText.invoiceNumber || undefined,
    loadedOut: loadedOutFilter,
    markedPicked: markedPickedFilter,
    scanStatus: scanStatusFilter,
    sortKey: sortConfig.key || undefined,
    sortDir: sortConfig.key ? sortConfig.direction : undefined,
    limit,
    offset,
  }), [
    filters.auctionGroupId,
    debouncedText,
    loadedOutFilter,
    markedPickedFilter,
    scanStatusFilter,
    sortConfig,
    limit,
    offset,
  ]);

  const { data: biddersPage, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: queryKeys.bidders(queryFilters),
    queryFn: () => getBidders(queryFilters),
    enabled: !!filters.auctionGroupId,
    placeholderData: keepPreviousData,
    // Filter keystrokes create many unique keys; don't keep them forever in memory
    gcTime: 60_000,
  });

  const serverBidders = useMemo(() => (
    Array.isArray(biddersPage?.items)
      ? biddersPage.items
      : Array.isArray(biddersPage)
        ? biddersPage
        : []
  ), [biddersPage]);

  useEffect(() => {
    const auctionGroupId = searchParams.get('auctionGroupId');
    if (auctionGroupId) {
      const parsedId = parseInt(auctionGroupId);
      setFilters(prev => ({ ...prev, auctionGroupId: parsedId }));
      setExpandedRows(new Set());
      setOffset(0);
    }
  }, [searchParams]);

  const auctionGroupId = queryFilters.auctionGroupId;
  const { format: printFormat } = usePrintFormatPreference();
  const { data: auctionGroup } = useQuery({
    queryKey: queryKeys.auctionGroup(auctionGroupId),
    queryFn: () => getAuctionGroupById(auctionGroupId),
    enabled: !!auctionGroupId,
  });

  const handleSort = (key) => {
    setSortConfig({
      key,
      direction: sortConfig.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc',
    });
  };

  const toggleRow = useCallback((bidderId) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(bidderId)) {
        next.delete(bidderId);
      } else {
        next.add(bidderId);
      }
      return next;
    });
  }, []);

  const handleScanItems = useCallback((bidderId) => {
    const groupId = filters.auctionGroupId;
    if (!groupId) {
      setScanGroupWarning('Select an auction group from Home before scanning items.');
      return;
    }
    setScanGroupWarning('');
    navigate(`/bidders/${bidderId}/scan?auctionGroupId=${groupId}`);
  }, [filters.auctionGroupId, navigate]);

  const handleOpenHistory = useCallback((bidder) => {
    setHistoryBidder({
      bidderId: bidder.bidder_id,
      bidderName: bidder.bidder_name,
    });
  }, []);

  /** Rewrite a bidder's flags in every cached page of the list. */
  const patchBidderInCache = useCallback((bidderId, changes) => {
    const patchList = (list) => {
      let changed = false;
      const next = list.map((row) => {
        if (String(row.bidder_id) !== String(bidderId)) return row;
        changed = true;
        return { ...row, ...changes };
      });
      return changed ? next : list;
    };

    queryClient.setQueriesData({ queryKey: ['bidders'] }, (old) => {
      if (!old) return old;
      if (Array.isArray(old)) return patchList(old);
      if (Array.isArray(old.items)) {
        const items = patchList(old.items);
        return items === old.items ? old : { ...old, items };
      }
      return old;
    });
  }, [queryClient]);

  const handlePickSynced = useCallback((entry, result) => {
    if (result && typeof result[entry.field] === 'boolean') {
      patchBidderInCache(entry.bidderId, { [entry.field]: result[entry.field] });
    }
    // Mark stale without refetching: the confirmed value is already in cache,
    // and the next filter or page change will pull fresh counts.
    queryClient.invalidateQueries({ queryKey: ['bidders'], refetchType: 'none' });
  }, [patchBidderInCache, queryClient]);

  // The server refused the write, so the optimistic row is now a lie. Pull the
  // real state back rather than leaving a status nobody saved.
  const handlePickRejected = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['bidders'] });
  }, [queryClient]);

  const pickQueueHandlers = useMemo(
    () => ({ onSynced: handlePickSynced, onRejected: handlePickRejected }),
    [handlePickSynced, handlePickRejected]
  );

  const {
    isOnline,
    pending: pendingPicks,
    pendingCount,
    syncing: syncingPicks,
    lastError: pickSyncError,
    flush: flushPicks,
  } = usePickQueue(pickQueueHandlers);

  const handleRetrySync = useCallback(() => {
    clearPickQueueError();
    flushPicks();
    refetch();
  }, [flushPicks, refetch]);

  // Rows reflect the click immediately; the write is queued and replayed until
  // the server confirms it, so a dropped connection never loses a pick.
  const applyPickChange = useCallback((bidder, field, nextValue) => {
    patchBidderInCache(bidder.bidder_id, { [field]: nextValue });
    enqueuePick({
      bidderId: bidder.bidder_id,
      auctionGroupId,
      field,
      value: nextValue,
    });
    flushPicks();
  }, [patchBidderInCache, auctionGroupId, flushPicks]);

  const handleToggleLoadedOut = useCallback((bidder) => {
    applyPickChange(bidder, PICK_FIELD_LOADED_OUT, !bidder.is_picked);
  }, [applyPickChange]);

  const handleToggleMarkedPicked = useCallback((bidder) => {
    applyPickChange(bidder, PICK_FIELD_MARKED_PICKED, !bidder.is_marked_picked);
  }, [applyPickChange]);

  // A queued write survives a refetch that has not caught up yet.
  const bidders = useMemo(
    () => applyPendingPicks(serverBidders, auctionGroupId, pendingPicks),
    [serverBidders, auctionGroupId, pendingPicks]
  );
  const totalBidders = typeof biddersPage?.total === 'number' ? biddersPage.total : bidders.length;

  const pendingPickKeys = useMemo(() => {
    const keys = new Set();
    for (const entry of pendingPicks) {
      if (auctionGroupId != null && entry.auctionGroupId !== auctionGroupId) continue;
      keys.add(`${entry.bidderId}:${entry.field}`);
    }
    return keys;
  }, [pendingPicks, auctionGroupId]);

  const isPickPending = (bidderId, field) =>
    pendingPickKeys.has(`${bidderId}:${field}`);

  const handleScanModeToggle = () => {
    setScanModeActive(!scanModeActive);
    if (!scanModeActive) {
      setScanInput('');
    }
  };

  const handleScanInputChange = (e) => {
    const value = clampInput(e.target.value);
    setScanInput(value);
    
    // Auto-detect when a complete pick-sheet QR URL is scanned
    if (isPickSheetScanUrl(value)) {
      handleQRCodeScan(value);
    }
  };

  const navigateToBidderScan = (bidderId, auctionGroupId, { fromCamera = false } = {}) => {
    if (!auctionGroupId) {
      alert('Unable to determine auction group. Please ensure you are viewing bidders for a specific auction group, or scan a QR code that includes the auction group ID.');
      return;
    }
    if (fromCamera) {
      setAutoOpenScanCamera();
    }
    navigate(`/bidders/${encodeURIComponent(bidderId)}/scan?auctionGroupId=${encodeURIComponent(auctionGroupId)}`);
    setScanModeActive(false);
    setScanInput('');
  };

  const handleQRCodeScan = async (qrCode, { fromCamera = false } = {}) => {
    if (scanLookupInFlightRef.current) return;
    try {
      const raw = String(qrCode || '').trim();
      if (!raw) return;

      if (isPickSheetScanUrl(raw)) {
        let urlString = raw;
        if (!urlString.startsWith('http') && !urlString.startsWith('/')) {
          urlString = '/' + urlString;
        }

        const urlMatch = urlString.match(/\/bidders\/([^\/\?]+)/);
        if (urlMatch && urlMatch[1]) {
          const bidderId = urlMatch[1];

          let auctionGroupId = null;
          try {
            if (urlString.startsWith('http')) {
              const url = new URL(urlString);
              auctionGroupId = url.searchParams.get('auctionGroupId');
            } else {
              const queryMatch = urlString.match(/[?&]auctionGroupId=([^&]+)/);
              if (queryMatch && queryMatch[1]) {
                auctionGroupId = queryMatch[1];
              }
            }
          } catch (e) {
            const queryMatch = urlString.match(/[?&]auctionGroupId=([^&]+)/);
            if (queryMatch && queryMatch[1]) {
              auctionGroupId = queryMatch[1];
            }
          }

          if (!auctionGroupId && filters.auctionGroupId) {
            auctionGroupId = String(filters.auctionGroupId);
          }

          navigateToBidderScan(bidderId, auctionGroupId, { fromCamera });
          return;
        }

        alert('Invalid QR code format. Expected format: /bidders/{bidderId}/scan or full URL');
        return;
      }

      const invoiceNumber = parseInvoiceBarcode(raw);
      if (!invoiceNumber) {
        alert('Invalid QR code or invoice barcode. Scan a bidder pick-sheet QR code or a paper invoice barcode.');
        return;
      }

      const auctionGroupId = filters.auctionGroupId;
      if (!auctionGroupId) {
        alert('Unable to determine auction group. Please ensure you are viewing bidders for a specific auction group.');
        return;
      }

      scanLookupInFlightRef.current = true;
      try {
        const result = await getBidderByInvoice(invoiceNumber, auctionGroupId);
        if (!result?.bidderId) {
          alert('No bidder found for this invoice number');
          return;
        }
        navigateToBidderScan(result.bidderId, auctionGroupId, { fromCamera });
      } catch (lookupErr) {
        alert(lookupErr.message || 'No bidder found for this invoice number');
      } finally {
        scanLookupInFlightRef.current = false;
      }
    } catch (err) {
      scanLookupInFlightRef.current = false;
      alert(`Error parsing QR code: ${err.message}`);
    }
  };

  const handleScanSubmit = (e) => {
    e.preventDefault();
    if (scanInput.trim()) {
      handleQRCodeScan(scanInput.trim());
    }
  };

  const handleClearFilters = () => {
    setFilters({
      bidderId: '',
      name: '',
      invoiceNumber: '',
      auctionGroupId: filters.auctionGroupId, // Preserve auctionGroupId from URL
    });
    setLoadedOutFilter('all');
    setMarkedPickedFilter('all');
    setScanStatusFilter('all');
    setSortConfig({ key: null, direction: 'asc' });
    setOffset(0);
  };

  if ((isLoading && !biddersPage) || (!filters.auctionGroupId && searchParams.get('auctionGroupId'))) {
    return (
      <div className="container">
        <div className="loading">
          <Loader className="spin" size={40} />
          <p>Loading bidders...</p>
        </div>
      </div>
    );
  }

  // Only take over the page when there is nothing to show. With cached rows on
  // screen the failure is reported in the status bar so work can continue.
  if (error && serverBidders.length === 0) {
    return (
      <div className="container">
        <div className="error">
          <strong>Error:</strong> {error.message}
        </div>
      </div>
    );
  }

  const listStale = !!error && serverBidders.length > 0;

  return (
    <div className="container">
      {scanGroupWarning && (
        <div className="error" style={{ marginBottom: '16px' }} role="alert">
          {scanGroupWarning}
        </div>
      )}
      {/* Floating Scan Mode Button */}
      <button
        onClick={handleScanModeToggle}
        className="scan-mode-button"
        style={{
          backgroundColor: scanModeActive ? '#28a745' : '#007bff',
        }}
      >
        <Scan size={18} style={{ flexShrink: 0 }} />
        <span className="scan-mode-button-text">
          {scanModeActive ? 'Scan Mode: ON' : 'Scan Mode'}
        </span>
      </button>

      {/* Scan Mode Popup */}
      {scanModeActive && (
        <div
          style={{
            position: 'fixed',
            top: '0',
            left: '0',
            right: '0',
            bottom: '0',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setScanModeActive(false);
            }
          }}
        >
          <div
            style={{
              backgroundColor: 'white',
              padding: '30px',
              borderRadius: '12px',
              boxShadow: '0 8px 16px rgba(0, 0, 0, 0.2)',
              minWidth: 0,
              width: 'min(400px, 90vw)',
              maxWidth: '90%',
              boxSizing: 'border-box'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Scan size={24} />
                Scan QR Code/Invoice Barcode
              </h3>
              <button
                onClick={() => setScanModeActive(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '5px',
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <X size={24} />
              </button>
            </div>
            <form onSubmit={handleScanSubmit}>
              <label style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold' }}>
                Scan QR Code/Invoice Barcode
              </label>
              <input
                type="text"
                className="input"
                value={scanInput}
                onChange={handleScanInputChange}
                placeholder="Scan QR Code/Invoice Barcode"
                maxLength={INPUT_MAX_LENGTH}
                autoFocus
                style={{ width: '100%', padding: '12px', fontSize: '16px', marginBottom: '15px' }}
              />
              
              <div style={{ marginBottom: '15px' }}>
                <Suspense fallback={<p style={{ margin: 0 }}>Loading scanner…</p>}>
                  <BarcodeScanner
                    isOpen={scanModeActive}
                    onScan={(scannedText) => {
                      const text = clampInput(scannedText);
                      setScanInput(text);
                      if (text.trim()) {
                        handleQRCodeScan(text.trim(), { fromCamera: true });
                      }
                    }}
                    onClose={() => {
                      // Scanner closed, no action needed
                    }}
                  />
                </Suspense>
              </div>
              
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => setScanModeActive(false)}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={!scanInput.trim()}
                >
                  Go to Scan Page
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="card">
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '20px', flexWrap: 'wrap' }}>
          <button
            onClick={() => navigate(APP_HOME)}
            className="btn"
            style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 16px' }}
          >
            <ArrowLeft size={16} />
            Back to Home
          </button>
          <h2 style={{ margin: 0 }}>👥 View Bidders</h2>
          {filters.auctionGroupId && (
            <h2 style={{ margin: 0, fontWeight: 'normal' }}>
              {decodeHtmlEntities(auctionGroup?.title) || `Auction Group #${filters.auctionGroupId}`}
            </h2>
          )}
          <div style={{ marginLeft: 'auto', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            {filters.auctionGroupId && (
              <>
                <button
                  onClick={() => navigate(`/merge?auctionGroupId=${filters.auctionGroupId}`)}
                  className="btn btn-primary"
                  style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 16px' }}
                >
                  <FileUp size={16} />
                  Merge Data & Settings
                </button>
                <button
                  onClick={() => navigate(`/high-volume?auctionGroupId=${filters.auctionGroupId}`)}
                  className="btn btn-warning"
                  style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 16px' }}
                >
                  <BarChart3 size={16} />
                  High Volume Report
                </button>
              </>
            )}
            <button
              onClick={() => {
                if (filters.auctionGroupId) {
                  navigate(`/no-bids?auctionGroupId=${filters.auctionGroupId}`);
                } else {
                  navigate('/no-bids');
                }
              }}
              className="btn btn-danger"
              style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 16px' }}
            >
              <Package size={16} />
              No-Bids List
            </button>
          </div>
        </div>

        <PrintFormatToggle style={{ marginBottom: '16px' }} />

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px', marginBottom: '20px' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              Search Bidder ID
            </label>
            <input
              type="text"
              className="input search-filter-input"
              placeholder="Enter bidder ID"
              value={filters.bidderId}
              onChange={(e) => setFilters({ ...filters, bidderId: clampInput(e.target.value) })}
              maxLength={INPUT_MAX_LENGTH}
              style={{ width: '100%' }}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              Search Bidder Name
            </label>
            <input
              type="text"
              className="input search-filter-input"
              placeholder="Enter name"
              value={filters.name}
              onChange={(e) => setFilters({ ...filters, name: clampInput(e.target.value) })}
              maxLength={INPUT_MAX_LENGTH}
              style={{ width: '100%' }}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              Search Invoice #
            </label>
            <input
              type="text"
              className="input search-filter-input"
              placeholder="Enter invoice number"
              value={filters.invoiceNumber}
              onChange={(e) => setFilters({ ...filters, invoiceNumber: clampInput(e.target.value) })}
              maxLength={INPUT_MAX_LENGTH}
              style={{ width: '100%' }}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              Filter by Loaded Out
            </label>
            <select
              className="input"
              value={loadedOutFilter}
              onChange={(e) => setLoadedOutFilter(e.target.value)}
              style={{ width: '100%', padding: '8px' }}
            >
              <option value="all">All Bidders</option>
              <option value="loaded">Loaded Out</option>
              <option value="not-loaded">Not Loaded Out</option>
            </select>
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              Filter by Picked/Scanned
            </label>
            <select
              className="input"
              value={
                markedPickedFilter !== 'all'
                  ? markedPickedFilter
                  : scanStatusFilter !== 'all'
                    ? scanStatusFilter
                    : 'all'
              }
              onChange={(e) => {
                const value = e.target.value;
                if (value === 'picked' || value === 'not-picked') {
                  setMarkedPickedFilter(value);
                  setScanStatusFilter('all');
                } else if (value === 'scanned' || value === 'partial' || value === 'not-scanned') {
                  setScanStatusFilter(value);
                  setMarkedPickedFilter('all');
                } else {
                  setMarkedPickedFilter('all');
                  setScanStatusFilter('all');
                }
              }}
              style={{ width: '100%', padding: '8px' }}
            >
              <option value="all">All Bidders</option>
              <optgroup label="Picked">
                <option value="picked">Picked</option>
                <option value="not-picked">Not Picked</option>
              </optgroup>
              <optgroup label="Scan Status">
                <option value="scanned">Scanned</option>
                <option value="partial">Partial Scan</option>
                <option value="not-scanned">Not Scanned</option>
              </optgroup>
            </select>
          </div>

          <div style={{ display: 'flex', alignItems: 'flex-end' }}>
            <button
              onClick={handleClearFilters}
              className="btn btn-secondary"
              style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', padding: '8px 16px' }}
            >
              <X size={16} />
              Clear Filters
            </button>
          </div>
        </div>

        <ConnectionStatusBar
          isOnline={isOnline}
          pendingCount={pendingCount}
          syncing={syncingPicks}
          syncError={pickSyncError}
          listStale={listStale}
          onRetrySync={handleRetrySync}
          onRetryList={refetch}
        />

        <div style={{ marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '12px' }}>
          <strong>Displaying {totalBidders} bidders</strong>
          {isFetching && (
            <span style={{ color: 'var(--color-gray-600)', fontSize: '13px', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
              <Loader className="spin" size={14} />
              Updating…
            </span>
          )}
        </div>

        <div className="table-wrapper" style={{ overflowX: 'auto' }}>
          <table className="table">
            <thead>
              <tr>
                <th onClick={() => handleSort('bidder_id')}>
                  Bidder ID {sortConfig.key === 'bidder_id' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </th>
                <th onClick={() => handleSort('bidder_name')}>
                  Bidder Name {sortConfig.key === 'bidder_name' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </th>
                <th onClick={() => handleSort('invoice_numbers')}>
                  Invoice # {sortConfig.key === 'invoice_numbers' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </th>
                <th onClick={() => handleSort('item_count')}>
                  Items {sortConfig.key === 'item_count' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </th>
                <th onClick={() => handleSort('scan_status')}>
                  Scan Status {sortConfig.key === 'scan_status' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                </th>
                <th className="actions-column-header">Actions</th>
              </tr>
            </thead>
            <tbody>
              {bidders.map((bidder) => (
                <BidderRow
                  key={`${bidder.bidder_id}-${filters.auctionGroupId || 'all'}`}
                  bidder={bidder}
                  auctionGroupId={auctionGroupId}
                  isMobile={isMobile}
                  isExpanded={expandedRows.has(bidder.bidder_id)}
                  printFormat={printFormat}
                  markedPickedPending={isPickPending(bidder.bidder_id, PICK_FIELD_MARKED_PICKED)}
                  loadedOutPending={isPickPending(bidder.bidder_id, PICK_FIELD_LOADED_OUT)}
                  onToggleRow={toggleRow}
                  onScanItems={handleScanItems}
                  onToggleMarkedPicked={handleToggleMarkedPicked}
                  onToggleLoadedOut={handleToggleLoadedOut}
                  onOpenHistory={handleOpenHistory}
                />
              ))}
            </tbody>
          </table>
        </div>

        <PaginationControls
          total={totalBidders}
          limit={limit}
          offset={offset}
          onChange={({ limit: nextLimit, offset: nextOffset }) => {
            setLimit(nextLimit);
            setOffset(nextOffset);
          }}
        />
      </div>

      {historyBidder && (
        <BidderHistoryModal
          bidderId={historyBidder.bidderId}
          bidderName={historyBidder.bidderName}
          onClose={() => setHistoryBidder(null)}
        />
      )}
    </div>
  );
}

export default ViewBidders;





